import React, {
    createContext,
    useContext,
    useRef,
    useCallback,
    useMemo,
    useEffect,
    useState
} from "react";
import isEqual from "lodash/isEqual";

// --- Configuration ---

type DebugLevels = "EXHAUSTIVE" | "TRACE" | "DEBUG" | "INFO" | "WARN" | "ERROR" | "FATAL";

export const DebugLevel: { [key in DebugLevels]: DebugLevels } = {
    EXHAUSTIVE: "EXHAUSTIVE",
    TRACE: "TRACE",
    DEBUG: "DEBUG",
    INFO: "INFO",
    WARN: "WARN",
    ERROR: "ERROR",
    FATAL: "FATAL"
};

const DEBUG_LEVEL_ORDER: { [key in DebugLevels]: number } = {
    EXHAUSTIVE: 6,
    TRACE: 5,
    DEBUG: 4,
    INFO: 3,
    WARN: 2,
    ERROR: 1,
    FATAL: 0
};

// Defines how state updates are propagated
export type UpdateMode = "immediate" | "debounced" | "manual";
// Type for a function that compares two values for equality
export type CompareFunction<T = any> = (a: T, b: T) => boolean;

// Options for configuring individual state hooks (useDirtyState/useDirtyStateO)
export interface UseDirtyStateOptions {
    /** A user-friendly name for this state, required for auto-save dirty payloads and useful for debugging. Defaults to the generated ID (not dirty payload compliant). */
    friendlyName?: string;
    /** How the manager updates its internal `currentValue` and the parent component's state when `update` is called. Defaults to 'immediate'. */
    updateMode?: UpdateMode;
    /** Debounce delay in milliseconds if `updateMode` is 'debounced'. Defaults to 300ms. */
    debounceMs?: number;
    /** Optional custom comparison function. If not provided, defaults to lodash.isEqual for objects/arrays and === for primitives. */
    compare?: CompareFunction;
    /** Is this an order sensitive array when performing comparisons? */
    isOrderSensitiveArray?: boolean;
}

// --- Types ---

// Internal structure to track the state of a registered value
interface TrackedStateEntry<T = any> {

    // --== Value State Tracking ==-- //
    /** The baseline value used for dirty checking. Updated by markClean or resetValue. */
    cleanValue: T;
    /** The value currently reflected in the parent component's state. Updated based on updateMode. */
    currentValue: T;
    /** The absolute latest value received via the `update` function, potentially not yet committed. */
    latestValue: T;
    /** The value provided when the state was initially registered. Used for revertToOriginal. */
    originalValue: T;
    /** React's state setter function for the parent component. */
    setState: React.Dispatch<React.SetStateAction<T>>;

    // --== Categorization ==-- //
    /** The original category name provided during registration. */
    friendlyCategory: string;
    /** Whitespace-removed version of the category name for internal lookups. */
    processedCategory: string;

    // --== Configuration ==-- //
    options: UseDirtyStateOptions;
    compare: CompareFunction<T>;

    // --== Dirty Status ==-- //
    isDirty: () => boolean;
}

// Map storing all tracked states, keyed by their unique ID
type DirtyStateMap = Map<string, TrackedStateEntry>;

// Callbacks that can be configured on the manager
interface DirtyStateCallbacks {
    /** Called when the manager transitions from having no dirty states to having at least one dirty state. */
    onDirty?: () => void;
    /** Called when the manager transitions from having dirty states to having no dirty states. */
    onClean?: () => void;
}

// Type alias for the dirty payload structure
export type DirtyPayload = Record<string, any>;

// Configuration for the auto-save feature
interface AutoSaveConfig {
    /** The interval in milliseconds for checking dirty/pending state and triggering the callback. */
    intervalMs?: number;
    /**
     * The callback function to execute when the auto-save interval triggers
     * and finds dirty state. Receives the dirty payload as an argument.
     */
    triggerCallback?: (payload: DirtyPayload) => void;
}

// ---=== Context Definition ===--- //

interface DirtyStateManagerContextValue {
    // State registration
    register: <T>(
        id: string,
        originalValue: T,
        setState: React.Dispatch<React.SetStateAction<T>>,
        friendlyCategory: string,
        options?: UseDirtyStateOptions
    ) => void;
    unregister: (id: string) => void;
    // State operations
    setLatestValue: <T>(id: string, newValue: T) => void;
    getLatestValue: <T>(id: string) => T | undefined;
    commitValue: (id: string) => void;
    forceCommitValue: <T>(id: string, newValue: T) => void; // Commits immediately, bypassing updateMode
    resetValue: <T>(id: string, newValue: T) => void; // Resets cleanValue and commits
    revertToOriginal: (idOrCategory?: string) => void;
    // Dirty state management
    isDirty: (idOrCategory?: string) => boolean; // Checks currentValue vs cleanValue
    isPending: (idOrCategory?: string) => boolean; // Checks latestValue vs currentValue
    getDirtyCategories: () => string[];
    markClean: (idOrCategory?: string | string[]) => void;
    // Auto-save operations
    markAutoSaveComplete: () => void;
    forceAutoSave: () => Promise<boolean>;
    isForcedAutoSaveInProgress: () => boolean;
    hasPendingForcedAutoSave: () => boolean;
    completeForcedAutoSave: (success: boolean) => void;
    // Manager operations
    pause: () => void;
    resume: () => void;
    // Configuration
    setAutoSaveConfig: (config: AutoSaveConfig) => void;
    setDebugMode: (level: DebugLevels) => void;
    setPassthroughMode: (active: boolean) => void;
    isPassthroughModeActive: () => boolean;
    setDirtyCallbacks: (callbacks: DirtyStateCallbacks) => void;
}

// The actual React context
const DirtyStateManagerContext = createContext<DirtyStateManagerContextValue | undefined>(undefined);

// ---=== Helper Functions ===--- //

// Symbol used temporarily for IDs before registration
const UNREGISTERED_ID = Symbol("UNREGISTERED_ID");
// Helper to normalize category names for internal use
const processCategoryName = (name: string): string => name.replace(/\s+/g, "");

const isEqualDeep = (a: any, b: any, options?: UseDirtyStateOptions): boolean => {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (typeof a !== typeof b) return false;

    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;
        const isOrderSensitive = options?.isOrderSensitiveArray === true;
        // Clone arrays before sorting if not order sensitive to avoid mutating original comparison values
        let arrayA = isOrderSensitive ? a : [...a].sort();
        let arrayB = isOrderSensitive ? b : [...b].sort();
        for (let i = 0; i < arrayA.length; i++) {
            // Recursively compare elements
            if (!isEqualDeep(arrayA[i], arrayB[i], options)) return false;
        }
        return true;
    }
    if (a instanceof Date && b instanceof Date) {
        return a.getTime() === b.getTime();
    }
    if (a instanceof RegExp && b instanceof RegExp) {
        return a.toString() === b.toString();
    }
    if (typeof a === "object" && typeof b === "object") {
        if (a.constructor !== b.constructor) return false;
        const keysA = Object.keys(a);
        const keysB = Object.keys(b);
        if (keysA.length !== keysB.length) return false;
        keysA.sort();
        keysB.sort();
        if (!isEqual(keysA, keysB)) return false;
        for (const key of keysA) {
            if (Object.prototype.hasOwnProperty.call(a, key) !== Object.prototype.hasOwnProperty.call(b, key)) return false;
            if (Object.prototype.hasOwnProperty.call(a, key)) {
                if (!isEqualDeep((a as any)[key], (b as any)[key], options)) return false;
            }
        }
        return true;
    }
    return false;
};

const deepClone = <T,>(value: T): T => {
    const refs = new Map();
    function cloneRecursive<U>(val: U): U {
        if (typeof val !== "object" || val === null) { return val; }
        if (refs.has(val)) { return refs.get(val); }
        if (val instanceof Date) { const c = new Date(val.getTime()); refs.set(val, c); return c as any; }
        if (val instanceof RegExp) { const c = new RegExp(val.source, val.flags); refs.set(val, c); return c as any; }
        if (Array.isArray(val)) { const c: any[] = []; refs.set(val, c); val.forEach((i,ix)=>c[ix]=cloneRecursive(i)); return c as any; }
        if (typeof val === "object") {
            let c: any;
            try { c = new (val.constructor as any)(); }
            catch (e) { c = Object.create(Object.getPrototypeOf(val)); console.warn("DSM: Deep cloning fallback:", val.constructor?.name, e); }
            refs.set(val, c);
            for (const k in val) { if (Object.prototype.hasOwnProperty.call(val, k)) c[k] = cloneRecursive(val[k]); }
            return c;
        }
        console.warn("DSM: Deep cloning unsupported type:", val); return val;
    }
    return cloneRecursive(value);
};

// ---=== Provider Component ===--- //

interface DirtyStateProviderProps {
    children: React.ReactNode;
    initialDebug?: DebugLevels;
    initialAutoSaveIntervalMs?: number;
    initialOnAutoSaveTrigger?: (payload: DirtyPayload) => void;
    initialPassthroughMode?: boolean;
}

export const DirtyStateManagerProvider: React.FC<DirtyStateProviderProps> = ({
    children,
    initialDebug = DebugLevel.ERROR,
    initialAutoSaveIntervalMs,
    initialOnAutoSaveTrigger,
    initialPassthroughMode = false
}) => {
    // --- Refs for Internal State ---
    const allStatesRef = useRef<DirtyStateMap>(new Map());
    const autoSaveIntervalRef = useRef(initialAutoSaveIntervalMs);
    const autoSaveTimeoutRef = useRef<number | null>(null);
    const callbacksRef = useRef<DirtyStateCallbacks>({});
    const debounceTimersRef = useRef<Map<string, number>>(new Map());
    const debugLevelRef = useRef<DebugLevels>(initialDebug);
    const forcedAutoSavePromiseRef = useRef<Promise<boolean> | null>(null);
    const forcedAutoSaveResolverRef = useRef<((success: boolean) => void) | null>(null);
    const isCurrentlyDirtyRef = useRef(false);
    const isPassthroughModeRef = useRef<boolean>(initialPassthroughMode);
    const isPausedRef = useRef(true);
    const onAutoSaveTriggerRef = useRef(initialOnAutoSaveTrigger);
    const inFlightValuesRef = useRef<Map<string, any>>(new Map());
    const lastAutoSavedIdsRef = useRef<string[]>([]); // *** NEW: Tracks IDs from the last auto-save ***

    // ---=== Logging ===--- //

    const logDebug = useCallback((level: DebugLevels, ...args: any[]) => {
        if (DEBUG_LEVEL_ORDER[debugLevelRef.current] >= DEBUG_LEVEL_ORDER[level]) {
            const levelLower = level.toLowerCase();
            if (levelLower === "warn" || levelLower === "error") {
                console[levelLower](`DSM [${level}]:`, ...args);
            }
            else {
                console.log(`DSM [${level}]:`, ...args);
            }
        }
    }, []);

    const setDebugMode = useCallback((level: DebugLevels) => {
        logDebug(DebugLevel.INFO, `Setting debug mode to: ${level}`);
        debugLevelRef.current = level;
    }, [logDebug]);

    const displayEntryHeader = useCallback((id: string, entry: TrackedStateEntry) => {
        return `[${id}:${entry.processedCategory}:${entry.options?.friendlyName || "unknown"}]`;
    }, []);

    const displayEntryValue = useCallback((value: any) => {
        if (value === undefined) return "undefined";
        if (value === null) return "null";
        if (typeof value === "string") return value.trim() === "" ? "'empty'" : `"${value}"`;
        if (typeof value === "number" || typeof value === "boolean" || typeof value === "symbol") return value.toString();
        try { return JSON.stringify(value); }
        catch (e) { return value.toString ? value.toString() : `displayEntryValue: [unstringifiable object] : ${e}`; }
    }, []);

    const displayEntry = useCallback((id: string, entry: TrackedStateEntry, displayCurrentValue: boolean=false) => {
        return `${displayEntryHeader(id, entry)}${displayCurrentValue ? ` ${displayEntryValue(entry.currentValue)}` : ""}]`;
    }, [displayEntryHeader, displayEntryValue]);

    const commitValueInternal = useCallback(<T,>(id: string, valueToCommit: T): boolean => {
        logDebug(DebugLevel.TRACE, `(*) commitValueInternal [${id}]: CALLED. Value to commit = ${displayEntryValue(valueToCommit)}`);
        const entry = allStatesRef.current.get(id) as TrackedStateEntry<T> | undefined;
        if (entry) {
            const clonedValueForManager = deepClone(valueToCommit);
            if (!entry.compare(entry.currentValue, clonedValueForManager)) {
                entry.currentValue = clonedValueForManager;
                logDebug(DebugLevel.TRACE, `(*) commitValueInternal(updating): ${displayEntry(id, entry, true)}`);
                entry.setState(deepClone(clonedValueForManager));
                return true;
            }
            logDebug(DebugLevel.TRACE, `commitValueInternal(unchanged): ${displayEntry(id, entry, true)}`);
        }
        else {
            logDebug(DebugLevel.TRACE, `commitValueInternal: No entry found [${id}]`);
        }
        return false;
    }, [logDebug, displayEntry, displayEntryValue]);

    const isDirty = useCallback((idOrCategory?: string): boolean => {
        if (isPassthroughModeRef.current) {
            logDebug(DebugLevel.DEBUG, `isDirty: Passthrough mode active, returning false.`);
            return false;
        }

        const states = allStatesRef.current;
        if (!idOrCategory) {
            for (const entry of states.values()) {
                if (!entry.compare(entry.cleanValue, entry.currentValue)) return true;
            }
            return false;
        }

        if (states.has(idOrCategory)) {
            const entry = states.get(idOrCategory)!;
            return !entry.compare(entry.cleanValue, entry.currentValue);
        }

        const cat = processCategoryName(idOrCategory);
        for (const entry of states.values()) {
            if (entry.processedCategory === cat && !entry.compare(entry.cleanValue, entry.currentValue)) return true;
        }
        return false;
    }, [logDebug]);

    const isPending = useCallback((idOrCategory?: string): boolean => {
        if (isPassthroughModeRef.current) return false;
        const states = allStatesRef.current;
        if (!idOrCategory) {
            for (const entry of states.values()) if (!entry.compare(entry.latestValue, entry.currentValue)) return true;
            return false;
        }
        if (states.has(idOrCategory)) {
            const entry = states.get(idOrCategory); return entry ? !entry.compare(entry.latestValue, entry.currentValue) : false;
        }
        const cat = processCategoryName(idOrCategory);
        for (const entry of states.values()) if (entry.processedCategory === cat && !entry.compare(entry.latestValue, entry.currentValue)) return true;
        return false;
    }, []);

    const getDirtyCategories = useCallback((): string[] => {
        if (isPassthroughModeRef.current) return [];
        const dirtyCategories = new Set<string>();
        for (const entry of allStatesRef.current.values()) {
            if (!entry.compare(entry.cleanValue, entry.currentValue)) dirtyCategories.add(entry.friendlyCategory);
        }
        return Array.from(dirtyCategories);
    }, []);

    const stableCheckAndTriggerCallbacks = useCallback(() => {
        if (isPassthroughModeRef.current) return;
        if (isPausedRef.current) {
            logDebug(DebugLevel.EXHAUSTIVE, "Processing paused, skipping checkAndTriggerCallbacks.");
            return;
        }
        const isNowDirty = isDirty();
        if (isNowDirty !== isCurrentlyDirtyRef.current) {
            logDebug(DebugLevel.TRACE, `Overall committed dirty status changed: ${isNowDirty}`);
            if (isNowDirty) {
                callbacksRef.current.onDirty?.();
            }
            else {
                callbacksRef.current.onClean?.();
            }
            isCurrentlyDirtyRef.current = isNowDirty;
        }
    }, [logDebug, isDirty]);

    const clearAutoSaveInterval = useCallback(() => {
        if (autoSaveTimeoutRef.current !== null) {
            logDebug(DebugLevel.TRACE, "Auto-save: Clearing existing interval timer.");
            window.clearTimeout(autoSaveTimeoutRef.current);
            autoSaveTimeoutRef.current = null;
        }
    }, [logDebug]);

    const checkAndTriggerAutoSave = useCallback((): boolean => {
        if (isPassthroughModeRef.current || isPausedRef.current) return false;

        const triggerCb = onAutoSaveTriggerRef.current;
        if (!triggerCb) return false;

        const states = allStatesRef.current;
        const payload: DirtyPayload = {};
        const dirtyIds: string[] = [];
        let hasDirtyItems = false;
        let needsCheckCallbacks = false;

        logDebug(DebugLevel.TRACE, `Auto-save: Starting check of ${states.size} entries.`);
        for (const [id, entry] of states.entries()) {
            let isEntryDirty = !entry.compare(entry.cleanValue, entry.currentValue);

            if (entry.options.updateMode === "manual" && !entry.compare(entry.latestValue, entry.currentValue)) {
                if (commitValueInternal(id, entry.latestValue)) needsCheckCallbacks = true;
                isEntryDirty = !entry.compare(entry.cleanValue, entry.currentValue);
            }

            if (isEntryDirty) {
                hasDirtyItems = true;
                const key = entry.options?.friendlyName ?? id;
                payload[key] = deepClone(entry.currentValue);
                dirtyIds.push(id);
                inFlightValuesRef.current.set(id, entry.currentValue);
                logDebug(DebugLevel.TRACE, `Auto-save check: Added dirty entry [${key}] to payload.`);
            }
        }

        if (needsCheckCallbacks) stableCheckAndTriggerCallbacks();

        if (hasDirtyItems) {
            logDebug(DebugLevel.DEBUG, "Auto-save check: Found dirty changes, triggering callback.", payload);
            lastAutoSavedIdsRef.current = dirtyIds; // *** Store the IDs internally ***
            triggerCb(payload); // *** No longer passing dirtyIds ***
            return true;
        }

        logDebug(DebugLevel.DEBUG, "Auto-save check: No dirty changes found.");
        return false;
    }, [logDebug, commitValueInternal, stableCheckAndTriggerCallbacks, displayEntry]);

    const resetAutoSaveTimer = useCallback(() => {
        clearAutoSaveInterval();
        if (isPassthroughModeRef.current) return;
        const intervalMs = autoSaveIntervalRef.current;
        const triggerCb = onAutoSaveTriggerRef.current;
        if (!isPausedRef.current && intervalMs && intervalMs > 0 && triggerCb) {
            logDebug(DebugLevel.EXHAUSTIVE, `Auto-save: Setting timer to ${intervalMs}ms.`);
            autoSaveTimeoutRef.current = window.setTimeout(() => {
                logDebug(DebugLevel.DEBUG, `Auto-save: Timer fired.`);
                if (checkAndTriggerAutoSave()) {
                    resetAutoSaveTimer();
                }
                else {
                    logDebug(DebugLevel.DEBUG, `Auto-save: State clean, timer stopped.`);
                }
            }, intervalMs);
        }
    }, [logDebug, checkAndTriggerAutoSave, clearAutoSaveInterval]);

    function forceAutoSave(): Promise<boolean> {
        if (forcedAutoSavePromiseRef.current) return forcedAutoSavePromiseRef.current;
        if (isPassthroughModeRef.current) return Promise.resolve(true);
        if (isPausedRef.current) return Promise.resolve(false);
        if (!onAutoSaveTriggerRef.current) return Promise.resolve(false);
        if (!isDirty()) return Promise.resolve(true);

        logDebug(DebugLevel.INFO, "forceAutoSave: Forcing immediate auto-save");
        clearAutoSaveInterval();

        forcedAutoSavePromiseRef.current = new Promise<boolean>((resolve) => {
            forcedAutoSaveResolverRef.current = (success: boolean) => {
                forcedAutoSavePromiseRef.current = null;
                forcedAutoSaveResolverRef.current = null;
                resolve(success);
            };
            if (!checkAndTriggerAutoSave()) {
                forcedAutoSaveResolverRef.current(true);
            }
        });
        return forcedAutoSavePromiseRef.current;
    }

    const completeForcedAutoSave = useCallback((success: boolean) => {
        if (forcedAutoSaveResolverRef.current) {
            logDebug(DebugLevel.DEBUG, `completeForcedAutoSave: Resolving promise with success=${success}`);
            forcedAutoSaveResolverRef.current(success);
        }
    }, [logDebug]);

    const hasPendingForcedAutoSave = useCallback(() => {
        return forcedAutoSavePromiseRef.current !== null;
    }, []);

    const isForcedAutoSaveInProgress = useCallback(() => {
        return forcedAutoSaveResolverRef.current !== null;
    }, []);

    const setAutoSaveConfig = useCallback((config: AutoSaveConfig) => {
        logDebug(DebugLevel.DEBUG, `Configuring Auto-save: interval=${config.intervalMs}, hasCallback=${!!config.triggerCallback}`);
        autoSaveIntervalRef.current = config.intervalMs;
        onAutoSaveTriggerRef.current = config.triggerCallback;
        resetAutoSaveTimer();
    }, [logDebug, resetAutoSaveTimer]);

    const register = useCallback(<T,>(
        id: string, originalValueInput: T, setState: React.Dispatch<React.SetStateAction<T>>,
        friendlyCategory: string, options: UseDirtyStateOptions = {}
    ) => {
        const processedCategory = processCategoryName(friendlyCategory);
        const states = allStatesRef.current;
        const combinedOptions: UseDirtyStateOptions = { updateMode: "immediate", debounceMs: 300, ...options };
        const clonedOriginal = deepClone(originalValueInput);

        const getDefaultCompareFunction = (initialVal: T): CompareFunction<T> => {
            if (typeof initialVal === "object" && initialVal !== null) {
                return (a, b) => isEqualDeep(a, b, combinedOptions);
            }
            return (a, b) => a === b;
        };

        const compareFn = combinedOptions.compare || getDefaultCompareFunction(clonedOriginal);

        const entryIsDirty = (): boolean => {
            if (isPassthroughModeRef.current) return false;
            const currentEntry = allStatesRef.current.get(id);
            return currentEntry ? !currentEntry.compare(currentEntry.cleanValue, currentEntry.currentValue) : false;
        };

        if (!states.has(id)) {
            states.set(id, {
                originalValue: clonedOriginal,
                cleanValue: clonedOriginal,
                currentValue: clonedOriginal,
                latestValue: clonedOriginal,
                setState,
                friendlyCategory,
                processedCategory,
                options: combinedOptions,
                compare: compareFn,
                isDirty: entryIsDirty
            });
            logDebug(DebugLevel.TRACE, `register(new): ${displayEntry(id, states.get(id)!)}`);
            setState(clonedOriginal);
            stableCheckAndTriggerCallbacks();
        }
        else {
            const entry = states.get(id)!;
            const oldSetState = entry.setState;
            Object.assign(entry, {
                setState, friendlyCategory, processedCategory, options: combinedOptions, compare: compareFn, isDirty: entryIsDirty
            });

            const newClonedOriginal = deepClone(originalValueInput);
            if (!entry.compare(entry.originalValue, newClonedOriginal) || oldSetState !== setState) {
                logDebug(DebugLevel.DEBUG, `register (update - reset): ${displayEntry(id, entry)}`);
                Object.assign(entry, { originalValue: newClonedOriginal, cleanValue: newClonedOriginal, latestValue: newClonedOriginal, currentValue: newClonedOriginal });
                setState(deepClone(newClonedOriginal));
                stableCheckAndTriggerCallbacks();
            }
        }
    }, [logDebug, displayEntry, stableCheckAndTriggerCallbacks]);

    const baseUnregister = useCallback((id: string) => {
        const entry = allStatesRef.current.get(id);
        if (entry) {
            logDebug(DebugLevel.TRACE, `unregister: ${displayEntry(id, entry)}`);
            allStatesRef.current.delete(id);
            const timer = debounceTimersRef.current.get(id);
            if (timer) { window.clearTimeout(timer); debounceTimersRef.current.delete(id); }
            if (!isPassthroughModeRef.current) resetAutoSaveTimer();
            return !isPassthroughModeRef.current && entry.isDirty();
        }
        return false;
    }, [logDebug, displayEntry, resetAutoSaveTimer]);

    const baseSetLatestValue = useCallback(<T,>(id: string, newValue: T): boolean => {
        const entry = allStatesRef.current.get(id) as TrackedStateEntry<T> | undefined;
        if (!entry) return false;

        if (isPassthroughModeRef.current) {
            entry.setState(deepClone(newValue));
            return true;
        }

        const clonedNewValue = deepClone(newValue);
        if (entry.compare(entry.latestValue, clonedNewValue)) return false;

        resetAutoSaveTimer();
        entry.latestValue = clonedNewValue;
        const { updateMode, debounceMs } = entry.options;

        if (updateMode === "immediate") {
            return commitValueInternal(id, entry.latestValue);
        }
        else if (updateMode === "debounced") {
            const existingTimer = debounceTimersRef.current.get(id);
            if (existingTimer) window.clearTimeout(existingTimer);
            const newTimerId = window.setTimeout(() => {
                if (commitValueInternal(id, entry.latestValue)) stableCheckAndTriggerCallbacks();
                debounceTimersRef.current.delete(id);
                resetAutoSaveTimer();
            }, debounceMs);
            debounceTimersRef.current.set(id, newTimerId);
        }
        return false;
    }, [logDebug, commitValueInternal, stableCheckAndTriggerCallbacks, resetAutoSaveTimer]);

    const getLatestValue = useCallback(<T,>(id: string): T | undefined => {
        const entry = allStatesRef.current.get(id) as TrackedStateEntry<T> | undefined;
        if (isPassthroughModeRef.current && entry) return deepClone(entry.currentValue);
        return entry ? deepClone(entry.latestValue) : undefined;
    }, []);

    const baseCommitValue = useCallback((id: string): boolean => {
        if (isPassthroughModeRef.current) return false;
        const entry = allStatesRef.current.get(id);
        if (entry) {
            const changed = commitValueInternal(id, entry.latestValue);
            const timer = debounceTimersRef.current.get(id);
            if (timer) { window.clearTimeout(timer); debounceTimersRef.current.delete(id); }
            resetAutoSaveTimer();
            return changed;
        }
        return false;
    }, [commitValueInternal, resetAutoSaveTimer]);

    const baseForceCommitValue = useCallback(<T,>(id: string, newValue: T): boolean => {
        const entry = allStatesRef.current.get(id) as TrackedStateEntry<T> | undefined;
        if (!entry) return false;
        if (isPassthroughModeRef.current) {
            entry.setState(deepClone(newValue));
            return true;
        }
        const clonedNewValue = deepClone(newValue);
        entry.latestValue = clonedNewValue;
        const changed = commitValueInternal(id, clonedNewValue);
        const timer = debounceTimersRef.current.get(id);
        if (timer) { window.clearTimeout(timer); debounceTimersRef.current.delete(id); }
        resetAutoSaveTimer();
        return changed;
    }, [commitValueInternal, resetAutoSaveTimer]);

    const baseResetValue = useCallback(<T,>(id: string, newValue: T): boolean => {
        const entry = allStatesRef.current.get(id) as TrackedStateEntry<T> | undefined;
        if (!entry) return false;
        if (isPassthroughModeRef.current) {
            entry.setState(deepClone(newValue));
            return true;
        }
        const clonedNewValue = deepClone(newValue);
        entry.latestValue = clonedNewValue;
        entry.cleanValue = clonedNewValue;
        const changed = commitValueInternal(id, clonedNewValue);
        const timer = debounceTimersRef.current.get(id);
        if (timer) { window.clearTimeout(timer); debounceTimersRef.current.delete(id); }
        resetAutoSaveTimer();
        return changed;
    }, [commitValueInternal, resetAutoSaveTimer]);

    const baseMarkClean = useCallback((idOrCategory?: string | string[]): boolean => {
        if (isPassthroughModeRef.current) return false;
        const states = allStatesRef.current;
        let needsCallbackCheck = false;
        let cleanedEntryCount = 0;

        const processEntry = (entry: TrackedStateEntry, entryId: string) => {
            const wasInFlight = inFlightValuesRef.current.has(entryId);
            const isCurrentlyDirty = !entry.compare(entry.cleanValue, entry.currentValue);

            if (wasInFlight) {
                const inFlightValue = inFlightValuesRef.current.get(entryId);
                const newCleanValue = deepClone(inFlightValue);
                logDebug(DebugLevel.TRACE, `markClean: Processing in-flight [${entryId}]. New baseline.`, newCleanValue);
                entry.cleanValue = newCleanValue;
                needsCallbackCheck = true;
                if (entry.compare(entry.currentValue, newCleanValue)) {
                    cleanedEntryCount++;
                    logDebug(DebugLevel.TRACE, `markClean: Entry [${entryId}] is now clean.`);
                }
                else {
                    logDebug(DebugLevel.INFO, `markClean: Entry [${entryId}] changed during save, remains dirty.`);
                }
                inFlightValuesRef.current.delete(entryId);
            }
            else if (isCurrentlyDirty) {
                logDebug(DebugLevel.TRACE, `markClean: Manually marking [${entryId}] as clean.`);
                entry.cleanValue = entry.currentValue;
                needsCallbackCheck = true;
                cleanedEntryCount++;
            }
        };

        logDebug(DebugLevel.DEBUG, `=== markClean(${idOrCategory ?? "all"}) ===`);
        if (!idOrCategory) {
            states.forEach(processEntry);
        }
        else if (Array.isArray(idOrCategory)) {
            idOrCategory.forEach(id => {
                const entry = states.get(id);
                if (entry) processEntry(entry, id);
            });
        }
        else if (states.has(idOrCategory)) {
            processEntry(states.get(idOrCategory)!, idOrCategory);
        }
        else {
            const cat = processCategoryName(idOrCategory);
            states.forEach((e, id) => { if (e.processedCategory === cat) processEntry(e, id); });
        }

        logDebug(DebugLevel.DEBUG, `=== markClean cleaned ${cleanedEntryCount} entries ===`);
        if (needsCallbackCheck) resetAutoSaveTimer();
        return needsCallbackCheck;
    }, [logDebug, resetAutoSaveTimer]);

    // *** NEW: Method to complete an auto-save operation ***
    const baseMarkAutoSaveComplete = useCallback(() => {
        const idsToClean = lastAutoSavedIdsRef.current;
        if (idsToClean.length === 0) {
            logDebug(DebugLevel.TRACE, "markAutoSaveComplete: No IDs from last auto-save to process.");
            return false;
        }
        logDebug(DebugLevel.INFO, `markAutoSaveComplete: Processing ${idsToClean.length} entries from the last auto-save.`);
        const needsCallback = baseMarkClean(idsToClean);
        lastAutoSavedIdsRef.current = []; // Clear the list after processing
        return needsCallback;
    }, [logDebug, baseMarkClean]);

    const baseRevertToOriginal = useCallback((idOrCategory?: string): boolean => {
        if (isPassthroughModeRef.current) return false;
        const states = allStatesRef.current;
        let changed = false;
        const revertEntry = (entry: TrackedStateEntry, entryId: string) => {
            const originalValueClone = deepClone(entry.originalValue);
            if (!entry.compare(entry.currentValue, originalValueClone) || !entry.compare(entry.latestValue, originalValueClone)) {
                entry.latestValue = originalValueClone;
                entry.cleanValue = originalValueClone;
                if (commitValueInternal(entryId, originalValueClone)) changed = true;
                const timer = debounceTimersRef.current.get(entryId);
                if (timer) { window.clearTimeout(timer); debounceTimersRef.current.delete(entryId); }
            }
        };

        if (!idOrCategory) {
            states.forEach(revertEntry);
        }
        else if (states.has(idOrCategory)) {
            revertEntry(states.get(idOrCategory)!, idOrCategory);
        }
        else {
            const cat = processCategoryName(idOrCategory);
            states.forEach((e, id) => { if (e.processedCategory === cat) revertEntry(e, id); });
        }
        if (changed) resetAutoSaveTimer();
        return changed;
    }, [commitValueInternal, resetAutoSaveTimer]);

    const baseSetDirtyCallbacks = useCallback((callbacks: DirtyStateCallbacks) => {
        callbacksRef.current = callbacks;
    }, []);

    const clearAllTimers = useCallback(() => {
        if (autoSaveTimeoutRef.current !== null) {
            window.clearTimeout(autoSaveTimeoutRef.current);
            autoSaveTimeoutRef.current = null;
        }
        debounceTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
        debounceTimersRef.current.clear();
    }, []);

    const pause = useCallback(() => {
        if (!isPausedRef.current) {
            logDebug(DebugLevel.INFO, "Pausing processing.");
            isPausedRef.current = true;
            clearAllTimers();
        }
    }, [logDebug, clearAllTimers]);

    const resume = useCallback(() => {
        if (isPausedRef.current) {
            logDebug(DebugLevel.INFO, "Resuming processing.");
            isPausedRef.current = false;
            stableCheckAndTriggerCallbacks();
            resetAutoSaveTimer();
        }
    }, [logDebug, stableCheckAndTriggerCallbacks, resetAutoSaveTimer]);

    const setPassthroughMode = useCallback((active: boolean) => {
        if (isPassthroughModeRef.current !== active) {
            isPassthroughModeRef.current = active;
            if (active) {
                clearAllTimers();
            }
            else {
                stableCheckAndTriggerCallbacks();
                resetAutoSaveTimer();
            }
        }
    }, [clearAllTimers, stableCheckAndTriggerCallbacks, resetAutoSaveTimer]);

    const isPassthroughModeActive = useCallback((): boolean => {
        return isPassthroughModeRef.current;
    }, []);

    useEffect(() => {
        return () => {
            clearAllTimers();
        };
    }, [clearAllTimers]);

    const contextValue = useMemo(() => {
        const stableUnregister = (id: string) => { if (baseUnregister(id)) stableCheckAndTriggerCallbacks(); };
        const stableMarkClean = (idOrCategory?: string | string[]) => { if (baseMarkClean(idOrCategory)) stableCheckAndTriggerCallbacks(); };
        const stableMarkAutoSaveComplete = () => { if (baseMarkAutoSaveComplete()) stableCheckAndTriggerCallbacks(); };
        const stableRevertToOriginal = (idOrCategory?: string) => { if (baseRevertToOriginal(idOrCategory)) stableCheckAndTriggerCallbacks(); };
        const stableSetDirtyCallbacks = (callbacks: DirtyStateCallbacks) => { baseSetDirtyCallbacks(callbacks); stableCheckAndTriggerCallbacks(); };
        const stableCommitValue = (id: string) => { if (baseCommitValue(id)) stableCheckAndTriggerCallbacks(); };
        const stableForceCommitValue = <T,>(id: string, newValue: T) => { if (baseForceCommitValue(id, newValue)) stableCheckAndTriggerCallbacks(); };
        const stableResetValue = <T,>(id: string, newValue: T) => { if (baseResetValue(id, newValue)) stableCheckAndTriggerCallbacks(); };
        const stableSetLatestValue = <T,>(id: string, newValue: T) => { if(baseSetLatestValue(id, newValue)) stableCheckAndTriggerCallbacks(); };

        return {
            register,
            unregister: stableUnregister,
            setLatestValue: stableSetLatestValue,
            getLatestValue,
            commitValue: stableCommitValue,
            forceCommitValue: stableForceCommitValue,
            resetValue: stableResetValue,
            revertToOriginal: stableRevertToOriginal,
            isDirty,
            isPending,
            getDirtyCategories,
            markClean: stableMarkClean,
            markAutoSaveComplete: stableMarkAutoSaveComplete,
            forceAutoSave,
            isForcedAutoSaveInProgress,
            hasPendingForcedAutoSave,
            completeForcedAutoSave,
            pause,
            resume,
            setAutoSaveConfig,
            setDebugMode,
            setPassthroughMode,
            isPassthroughModeActive,
            setDirtyCallbacks: stableSetDirtyCallbacks
        };
    }, [
        register, baseUnregister, baseSetLatestValue, getLatestValue, baseCommitValue, baseForceCommitValue, baseResetValue, baseRevertToOriginal,
        isDirty, isPending, getDirtyCategories, baseMarkClean, baseMarkAutoSaveComplete,
        forceAutoSave, isForcedAutoSaveInProgress, hasPendingForcedAutoSave, completeForcedAutoSave,
        pause, resume, setAutoSaveConfig, setDebugMode, setPassthroughMode, isPassthroughModeActive, baseSetDirtyCallbacks, stableCheckAndTriggerCallbacks
    ]);

    return (
        <DirtyStateManagerContext.Provider value={contextValue}>
            {children}
        </DirtyStateManagerContext.Provider>
    );
};

export const useDirtyStateManager = () => {
    const context = useContext(DirtyStateManagerContext);
    if (context === undefined) {
        throw new Error("useDirtyStateManager must be used within a DirtyStateManagerProvider");
    }
    return context;
};

let idCounter = 0;
const generateUniqueId = (): string => `dsm-${idCounter++}-${Math.random().toString(36).substring(2, 9)}`;

export interface UseDirtyStateObjectReturn<T> {
    value: T;
    set: (newValueOrFn: React.SetStateAction<T>) => void;
    update: (newValueOrFn: React.SetStateAction<T>) => void;
    reset: (newValueOrFn: React.SetStateAction<T>) => void;
    latest: (options?: { commit?: boolean }) => T | undefined;
    commit: () => void;
}

export type UseDirtyStateArrayReturn<T> = [
    value: T,
    set: (newValueOrFn: React.SetStateAction<T>) => void,
    update: (newValueOrFn: React.SetStateAction<T>) => void,
    reset: (newValueOrFn: React.SetStateAction<T>) => void,
    latest: (options?: { commit?: boolean }) => T | undefined,
    commit: () => void
];

const processDirtyStateArgs = (
    friendlyNameOrOptions?: string | UseDirtyStateOptions,
    optionsOnly?: UseDirtyStateOptions,
    id?: string
): UseDirtyStateOptions => {
    let name: string | undefined;
    let baseOptions: UseDirtyStateOptions;

    if (typeof friendlyNameOrOptions === "string") {
        name = friendlyNameOrOptions;
        baseOptions = optionsOnly ?? {};
    }
    else if (typeof friendlyNameOrOptions === "object" && friendlyNameOrOptions !== null) {
        baseOptions = friendlyNameOrOptions;
        name = baseOptions.friendlyName;
    }
    else {
        baseOptions = optionsOnly ?? {};
        name = baseOptions.friendlyName;
    }

    return {
        updateMode: "immediate",
        debounceMs: 300,
        isOrderSensitiveArray: false,
        ...baseOptions,
        friendlyName: name ?? id ?? "unnamed-dsm-state"
    };
};

export const useDirtyStateO = <T,>(
    initialValueOrFn: T | (() => T),
    friendlyCategory: string,
    friendlyNameOrOptions?: string | UseDirtyStateOptions,
    optionsOnly?: UseDirtyStateOptions
): UseDirtyStateObjectReturn<T> => {
    const manager = useDirtyStateManager();
    const idRef = useRef<string>(generateUniqueId());
    const id = idRef.current;

    const initialValue = useMemo(() => {
        const val = typeof initialValueOrFn === "function"
            ? (initialValueOrFn as () => T)()
            : initialValueOrFn;
        return deepClone(val);
    }, []);

    const [value, setValue] = useState<T>(() => deepClone(initialValue));
    const finalOptions = useMemo(() => processDirtyStateArgs(friendlyNameOrOptions, optionsOnly, id), [friendlyNameOrOptions, optionsOnly, id]);

    useEffect(() => {
        manager.register<T>(id, initialValue, setValue, friendlyCategory, finalOptions);
        manager.resume();
        return () => {
            manager.unregister(id);
        };
    }, [manager, id, friendlyCategory, initialValue, finalOptions]);

    const set = useCallback((newValueOrFn: React.SetStateAction<T>) => {
        const newValue = typeof newValueOrFn === "function" ? (newValueOrFn as (prevState: T) => T)(value) : newValueOrFn;
        manager.forceCommitValue<T>(id, newValue);
    }, [manager, id, value]);

    const update = useCallback((newValueOrFn: React.SetStateAction<T>) => {
        const currentLatest = manager.getLatestValue<T>(id) ?? value;
        const newValue = typeof newValueOrFn === "function" ? (newValueOrFn as (prevState: T) => T)(currentLatest) : newValueOrFn;
        manager.setLatestValue<T>(id, newValue);
    }, [manager, id, value]);

    const reset = useCallback((newValueOrFn: React.SetStateAction<T>) => {
        const newValue = typeof newValueOrFn === "function" ? (newValueOrFn as (prevState: T) => T)(value) : newValueOrFn;
        manager.resetValue<T>(id, newValue);
    }, [manager, id, value]);

    const latest = useCallback((options?: { commit?: boolean }): T | undefined => {
        const commitFlag = options?.commit ?? false;
        const latestVal = manager.getLatestValue<T>(id);
        if (commitFlag && latestVal !== undefined) {
            manager.commitValue(id);
        }
        return latestVal;
    }, [manager, id]);

    const commit = useCallback(() => {
        manager.commitValue(id);
    }, [manager, id]);

    return useMemo(() => ({
        value, set, update, reset, latest, commit
    }), [value, set, update, reset, latest, commit]);
};

export const useDirtyState = <T,>(
    initialValueOrFn: T | (() => T),
    friendlyCategory: string,
    friendlyNameOrOptions?: string | UseDirtyStateOptions,
    optionsOnly?: UseDirtyStateOptions
): UseDirtyStateArrayReturn<T> => {
    const stateObject = useDirtyStateO<T>(initialValueOrFn, friendlyCategory, friendlyNameOrOptions, optionsOnly);
    return useMemo(() => [
        stateObject.value, stateObject.set, stateObject.update,
        stateObject.reset, stateObject.latest, stateObject.commit
    ], [stateObject]);
};
